import throttle from 'lodash/throttle';
import generateId from '../utils/generateId';
import { NavigationContext, FDPEvent } from './types';

/**
 * @description Base class for platform-specific trackers
 */
export default abstract class PlatformTracker {
  protected events: Array<FDPEvent>;
  protected sendDataThrottled;

  constructor(interval = 3000) {
    this.events = [];
    this.sendDataThrottled = throttle(
      () => {
        this.sendData();
      },
      interval,
      {
        leading: false,
        trailing: true
      }
    );
    window.addEventListener('beforeunload', (event: BeforeUnloadEvent) =>
      this._handleBeforeUnload(event)
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private _handleBeforeUnload(_event: BeforeUnloadEvent): void {
    this.flushEvents();
  }

  protected getDefaultNavigationContext(): NavigationContext {
    return {
      iid: generateId(),
      fm: 'organic',
      pn: 'None',
      pt: 'None'
    };
  }
  protected abstract setNavigationContext(nc: NavigationContext): void;
  public abstract getNavigationContext(): Promise<NavigationContext>;
  public async updateNavigationContext(nc: NavigationContext): Promise<void> {
    this.flushEvents();
    const oldNc = await this.getNavigationContext();
    this.setNavigationContext({
      ...oldNc,
      ppt: oldNc.pt,
      ppn: oldNc.pn,
      ...nc
    });
  }

  /**
   * @description Pushes an event into the events array
   * @param event - FDP event
   * @param flush - This boolean flag flushes all the events to the server and empties the events array.
   */
  public trackEvent(event: FDPEvent, flush = false): void {
    if (!event.en && !event._ev) {
      return;
    }
    event.t = Date.now();
    this.storeEvent(event);
    this.sendDataThrottled();
    if (flush) {
      this.flushEvents();
    }
  }

  protected storeEvent(event: FDPEvent) {
    this.events.push(event);
  }

  protected abstract sendData(): void;
  /**
   * @description Sends the list of events to the server
   */
  protected flushEvents() {
    this.sendDataThrottled.flush();
  }
}
